// DynamicChart.jsx
import React, { useRef } from "react";
import { Row, Col, Spin } from "antd";
import UniversalChart from "./UniversalChart";
import { resolveCondition, safePeek } from "../../helpers/helper";
import configs from "./dashboard.json";

/**
 * NOTE:
 * - resolveData is intentionally an empty stub. Do NOT add path-resolving logic here.
 * - The component WILL call resolveData(datasourceName, dataPath, ctx) for each widget.
 * - Replace/override resolveData externally when you need real data.
 */

// ===== resolveData stub (NO implementation here) =====
function resolveData(dataSource, path) {
  if (dataSource !== undefined && typeof path === "string") {
    const p = path.split(".");
    return safePeek(dataSource, p);
  }
  return undefined;
}

/* -----------------------------
        DynamicChart component
------------------------------ */
export default function DynamicDashboard({
  dashboard, // either a key into configs or a dashboard object
  containerStyle = {},
  dataSource = {},
  // optional context param that will be forwarded to resolveData if you later implement it
}) {
  const containerRef = useRef(null);

  // allow passing key in or the object directly
  const cfg = typeof dashboard === "string" ? configs[dashboard] : dashboard;

  // fallback: if cfg missing or bad, show message
  if (!cfg || !Array.isArray(cfg.rows)) {
    return <div style={{ padding: 12 }}>No dashboard configured</div>;
  }

  // Loading indicator logic: show spinner when dataSource is empty
  const hasDataSource = dataSource && Object.keys(dataSource).length > 0;

  // Card styles (visual only; do NOT change heights/widths)
  const baseCardStyle = {
    background: "#fff",
    borderRadius: 10,
    padding: 12,
    boxShadow: "0 6px 18px rgba(16,24,40,0.04)",
    border: "1px solid rgba(15,23,36,0.04)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
  };

  const compactCardStyle = {
    ...baseCardStyle,
    padding: 8,
    borderRadius: 8,
    boxShadow: "0 3px 8px rgba(16,24,40,0.04)",
  };

  return (
    <div
      ref={containerRef}
      className="p-1 mt-2"
      style={{
        width: "100%",
        ...containerStyle,
      }}
    >
      {!hasDataSource && (
        <div style={{ padding: 12 }}>
          <Spin>Loading Dashboard....</Spin>
        </div>
      )}

      {hasDataSource &&
        cfg.rows.map((row, ri) => {
          const maxH = row.maxHeight || undefined;
          const rowCompact = !!row.compact; // row-level compact flag

          return (
            <div
              key={ri}
              data-row-index={ri}
              style={{
                maxHeight: maxH,
                marginBottom: 12,
                width: "100%",
              }}
            >
              <Row gutter={[4, 4]} className="p-1">
                {row.columns.map((col, ci) => {
                  const span = col.colSpan || 12;
                  const colCompact = !!col.compact; // column-level compact flag
                  const compact = rowCompact || colCompact;

                  // optional explicit display value path
                  let displayValue;
                  if (col.displayValuePath) {
                    displayValue = resolveData(dataSource, col.displayValuePath);
                  }

                  // CALL resolveData (stub)
                  const raw = resolveData(dataSource,  col.dataPath);
                  if (col.condition){
                    const [field , condition] = col.condition.split("::")
                    const fieldValue = resolveData(dataSource,field)
                    if (!resolveCondition(fieldValue , condition)){
                        return <></>
                    }
                  }
                  // Choose card style
                  const cardStyle = compact ? compactCardStyle : baseCardStyle;

                  // Render the card; do NOT render title/subtitle here — UniversalChart handles that
                  return (
                    <Col key={ci} span={span}>
                      <div style={cardStyle}>
                        {/* Content area: chart / metric or placeholder */}
                        <div style={{ width: "100%",  minHeight: 1 }}>
                          {raw && (compact || col.type === "metric" || col.type === "metricGraph") ? (
                            <UniversalChart
                              seriesKey={col.seriesMapping}
                              valueMapping={col.valueMapping}
                              displayValue={displayValue}
                              type={"metric"}
                              subType={col.type}
                              data={raw}
                              title={col.title} // pass title so UniversalChart can render it
                              markHighest={col.markHighest ?? true}
                              // forward optional graph subtype props
                              graphType={col.subtype || col.graphType}
                              graphWidth={col.graphWidth}
                              graphHeight={col.graphHeight}
                              formatter={col.formatter}
                            />
                          ) : (raw != undefined )? (
                            // Non-metric charts
                            <UniversalChart
                              type={col.type}
                              data={raw}
                              title={col.title} // pass title to chart component
                              seriesKeys={col.seriesKeys || undefined}

                              style={{ width: "100%",height:col.height }}
                            />
                          ) : (
                            // Empty placeholder if no data
                            <div style={{ color: "#9ca3af", fontSize: 12 }}>
                              {col.emptyText ?? "No data"}
                            </div>
                          )}
                        </div>

                        {/* Optional footnote/subtitle left to UniversalChart; don't duplicate here */}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        })}
    </div>
  );
}
